// client/src/utils/moduleGeometry.js
// Итерация 8.1 + 8.5: единый источник геометрии для модулей структуры.
//
// Источники (приоритет):
//   0. module.visual_representation.geometry.primitive_type  ← для Ангары / старых модулей
//   1. module.base_form                                       ← для игры (installed_modules)
//   2. module.purpose → PURPOSE_TO_SHAPE → SHAPE_TO_PRIMITIVE
//   3. default → 'box'
//
// Размеры (приоритет):
//   0. visual_representation.geometry.dimensions (width/height/depth)
//   1. base_stats.volume_m3 → cbrt(volume) → side
//   2. slot_size → SLOT_VOLUMES → cbrt(volume)
//   3. default → 2
//
// Цвет:
//   0. visual_representation.material.color
//   1. visual.colors.primary
//   2. COLOR_BY_PURPOSE[module.purpose]
//   3. '#666666'
//
// ⚠️ Чистые данные — без React, без Three.js, без window.
//    Переиспользуется в Module3DViewer (Ангара), InteriorVisualizer (игра),
//    InstancedModules (LOD), Unreal (C++ порт).

// ============================================================================
// 1. SHAPE_TO_PRIMITIVE (base_form → primitive_type)
// ============================================================================

export const SHAPE_TO_PRIMITIVE = {
  // --- Энергетика ---
  fusion_reactor: 'sphere',
  annihilation_reactor: 'sphere',
  solar_panel: 'box',
  solar_array: 'box',
  geothermal: 'cylinder',
  wind_turbine: 'cylinder',
  hydro_turbine: 'cylinder',
  battery: 'box',
  capacitor: 'cylinder',
  rtg: 'cylinder',
  fission_reactor: 'sphere',
  zero_point: 'sphere',
  dark_energy: 'sphere',
  quantum_reactor: 'sphere',

  // --- Двигатели ---
  chemical_engine: 'cone',
  ion_engine: 'cone',
  plasma_engine: 'cone',
  photon_engine: 'cone',
  ftl_drive: 'cylinder',
  maneuvering_thruster: 'cone',
  maneuvering_thruster_bank: 'box',
  turbofan: 'cylinder',
  waterjet: 'cylinder',
  antigravity: 'sphere',

  // --- Жизнеобеспечение ---
  air_recycler: 'box',
  water_recycler: 'box',
  water_extractor: 'box',
  life_support_industrial: 'box',
  biosphere: 'sphere',
  biosphere_large: 'sphere',
  medbay: 'box',
  medbay_advanced: 'box',
  hospital: 'box',
  surgery: 'box',
  cryo_pod: 'cylinder',
  cryo_bay: 'box',
  stasis: 'cylinder',

  // --- Обитание ---
  crew_quarters: 'box',
  luxury_suite: 'box',
  barracks: 'box',
  mess_hall: 'box',
  recreation: 'box',
  gym: 'box',
  lounge: 'box',
  observation_deck: 'box',
  alien_habitat: 'box',
  zero_g: 'sphere',
  underwater_habitat: 'sphere',
  habitat_pod: 'sphere',
  mobile_base: 'box',
  shelter: 'box',
  living_zone: 'box',
  captain_quarters: 'box',
  officers_mess: 'box',

  // --- Склады ---
  cargo_bay: 'box',
  container: 'box',
  fuel_tank_chem: 'cylinder',
  fuel_tank_cryo: 'cylinder',
  antimatter_containment: 'sphere',
  fuel_depot: 'cylinder',
  ammo_rack: 'box',
  ammo_bunker: 'box',
  missile_rack: 'box',
  torpedo_rack: 'box',
  drone_storage: 'box',
  vault: 'box',
  artifact_container: 'box',
  bio_storage: 'box',
  waste_tank: 'cylinder',
  liquid_tank: 'cylinder',
  bulk_hold: 'box',
  hazardous_bay: 'box',

  // --- Производство ---
  ammo_factory: 'box',
  missile_assembly: 'box',
  slug_foundry: 'box',
  drone_fabricator: 'box',
  fabricator: 'box',
  industrial_fabricator: 'box',
  nanoforge: 'sphere',
  recycler: 'cylinder',
  smelter: 'cylinder',
  refinery: 'cylinder',
  electronics_lab: 'box',
  chemical_plant: 'cylinder',
  biolab: 'box',
  foundry: 'cylinder',
  assembler: 'box',
  mining_laser: 'cylinder',
  mining_drill: 'cylinder',
  ore_processor: 'box',

  // --- Ангары / доки ---
  shuttle_hangar: 'box',
  hangar: 'box',
  fighter_hangar: 'box',
  hangar_complex: 'box',
  fighter_bay: 'box',
  shuttle_bay: 'box',
  docking_port: 'torus',
  docking_bay: 'torus',
  large_docking_bay: 'torus',
  docking_complex: 'torus',
  repair_dock: 'torus',
  airlock_chamber: 'torus',
  cargo_airlock: 'torus',
  emergency_airlock: 'torus',
  airlock_complex: 'torus',

  // --- Оружие ---
  laser_turret: 'cylinder',
  laser_cannon: 'cylinder',
  heavy_laser: 'cylinder',
  pulse_laser: 'cylinder',
  missile_launcher: 'box',
  heavy_missile_launcher: 'box',
  torpedo_launcher: 'box',
  swarm_launcher: 'box',
  missile_silo: 'cylinder',
  railgun: 'cylinder',
  gauss_cannon: 'cylinder',
  coilgun: 'cylinder',
  mass_driver: 'cylinder',
  plasma_cannon: 'cylinder',
  plasma_bombard: 'cylinder',
  shield_generator: 'sphere',
  heavy_shield: 'sphere',
  armor_plate: 'box',
  ablative_armor: 'box',
  ciws: 'cylinder',
  point_defense: 'cylinder',
  ion_cannon: 'cylinder',
  disruptor: 'cylinder',
  tractor_beam: 'cone',
  gravity_gun: 'sphere',

  // --- Наука / сенсоры ---
  scanner: 'sphere',
  long_range_scanner: 'sphere',
  military_scanner: 'sphere',
  telescope: 'cylinder',
  gravimetric_scanner: 'sphere',
  stealth_detector: 'sphere',
  research_lab: 'box',
  research_complex: 'box',
  xeno_lab: 'box',
  physics_lab: 'box',
  nanotech_lab: 'box',
  observatory: 'sphere',
  probe_bay: 'box',
  artifact_lab: 'box',
  anomaly_analyzer: 'box',
  data_center: 'box',

  // --- Командование ---
  bridge: 'cone',
  captain_bridge: 'cone',
  flagship_bridge: 'cone',
  war_room: 'box',
  ai_core: 'sphere',
  computer_core: 'box',
  neural_net: 'sphere',
  quantum_computer: 'sphere',
  comms_array: 'cone',
  laser_comms: 'cone',
  ftl_comms: 'cone',
  relay: 'cone',
  encryption_center: 'box',
  secondary_bridge: 'cone',
  navigation_room: 'box',
  comm_center: 'box',

  // --- Поверхность ---
  dome: 'sphere',
  landing_pad: 'cylinder',
  garage: 'box',
  silo: 'cylinder',
  mining_rig: 'cylinder',
  excavator: 'box',
  quarry: 'cylinder',
  pump_station: 'cylinder',
  harvester: 'box',
  turret: 'cylinder',
  planetary_shield: 'sphere',
  bunker: 'box',
  greenhouse: 'sphere',
  water_purifier: 'box',
  substation: 'box',
  depot: 'box',

  // --- Спец ---
  stealth_generator: 'sphere',
  ecm: 'cylinder',
  decoy_launcher: 'box',
  gravity_gen: 'sphere',
  inertial_damper: 'sphere',
  stasis_field: 'sphere',
  simulator: 'box',
  vr_arena: 'sphere',
  casino: 'box',
  theater: 'box',
  park: 'sphere',

  // --- Связующие ---
  corridor: 'box',
  turbolift: 'cylinder',
  cargo_elevator: 'cylinder',
  emergency_passage: 'box',

  // --- Залы ---
  hall: 'box',

  // --- Новые спец ---
  armory: 'box',
  briefing_room: 'box',
  damage_control: 'box',
  security_center: 'box',
  brig: 'box',
  chapel: 'cone',
  hydroponics: 'box',
  waste_recycler: 'cylinder',
};

// ============================================================================
// 2. PURPOSE_TO_SHAPE (fallback: purpose → base_form)
// ============================================================================

export const PURPOSE_TO_SHAPE = {
  energy: 'fusion_reactor',
  energy_storage: 'battery',
  propulsion: 'chemical_engine',
  life_support: 'air_recycler',
  medical: 'medbay',
  habitation: 'crew_quarters',
  cargo: 'cargo_bay',
  fuel_storage: 'fuel_tank_chem',
  ammo_storage: 'ammo_rack',
  manufacturing: 'fabricator',
  ammo_factory: 'ammo_factory',
  mining: 'mining_laser',
  agriculture: 'greenhouse',
  hangar_bay: 'hangar',
  docking_bay: 'docking_bay',
  repair: 'repair_dock',
  airlock: 'airlock_chamber',
  weapon_system: 'laser_turret',
  shield_generator: 'shield_generator',
  armor: 'armor_plate',
  sensor: 'scanner',
  research: 'research_lab',
  command: 'bridge',
  communication: 'comms_array',
  connector: 'corridor',
  hall: 'hall',
  structure: 'dome',
  storage: 'cargo_bay',
  special: 'simulator',
};

// ============================================================================
// 3. COLOR_BY_PURPOSE (fallback)
// ============================================================================

export const COLOR_BY_PURPOSE = {
  energy: '#00e5ff',
  energy_storage: '#00b8d4',
  propulsion: '#ff9800',
  life_support: '#6aa0d8',
  medical: '#e91e63',
  habitation: '#4a7fb5',
  cargo: '#ff9800',
  fuel_storage: '#ffab00',
  ammo_storage: '#ef4444',
  manufacturing: '#ffd600',
  ammo_factory: '#d946ef',
  mining: '#a16207',
  agriculture: '#00e676',
  hangar_bay: '#3b82f6',
  docking_bay: '#a855f7',
  repair: '#22c55e',
  airlock: '#6aa0d8',
  weapon_system: '#ef4444',
  shield_generator: '#60a5fa',
  armor: '#94a3b8',
  sensor: '#22c55e',
  research: '#a855f7',
  command: '#fbbf24',
  communication: '#38bdf8',
  connector: '#64748b',
  hall: '#94a3b8',
  structure: '#cbd5e1',
  storage: '#f59e0b',
  special: '#f472b6',
  default: '#666666',
};

// ============================================================================
// 4. SLOT_VOLUMES (из ModuleTemplates.ex)
// ============================================================================

export const SLOT_VOLUMES = {
  '1x': 8,
  '2x': 125,
  '3x': 1000,
  '4x': 8000,
  '5x': 64000,
  '6x': 512000,
};

// ============================================================================
// 5. computeArgsFromDims (для visual_representation)
// ============================================================================

function computeArgsFromDims(primitive, dims) {
  const w = Math.max(0.5, dims.width || 2);
  const h = Math.max(0.5, dims.height || 2);
  const d = Math.max(0.5, dims.depth || 2);

  switch (primitive) {
    case 'box':
      return [w, h, d];
    case 'sphere':
      return [w / 2, 16, 16];
    case 'cylinder':
      return [w / 2, w / 2, h, 12];
    case 'cone':
      return [w / 2, h, 12];
    case 'torus':
      return [w / 2, Math.max(0.1, w / 10), 8, 16];
    default:
      return [w, h, d];
  }
}

// ============================================================================
// 6. ГЛАВНАЯ ФУНКЦИЯ
// ============================================================================

/**
 * Возвращает геометрию для модуля.
 *
 * @param {Object} module — экземпляр модуля.
 * @returns {{
 *   type: 'box'|'sphere'|'cylinder'|'cone'|'torus',
 *   args: number[],
 *   material: { color: string, emissive: string|null, emissiveIntensity: number, metalness: number, roughness: number },
 *   source: 'visual_representation'|'base_form'|'purpose'|'default',
 *   side: number,
 * }}
 */
export function getModuleGeometry(module) {
  if (!module || typeof module !== 'object') {
    return defaultGeometry();
  }

  // ============================================================
  // 0. visual_representation (приоритет для Ангары / старых модулей)
  // ============================================================
  const vr = module.visual_representation;
  if (vr?.geometry?.primitive_type) {
    const primitive = vr.geometry.primitive_type;
    const dims = vr.geometry.dimensions || { width: 2, height: 2, depth: 2 };
    const side = Math.max(
      dims.width || 2,
      dims.height || 2,
      dims.depth || 2
    );

    const color =
      vr.material?.color ||
      module.visual?.colors?.primary ||
      COLOR_BY_PURPOSE[module.purpose] ||
      COLOR_BY_PURPOSE.default;

    const material = {
      color,
      emissive: vr.material?.emissive || null,
      emissiveIntensity: vr.material?.emissiveIntensity || 0,
      metalness: vr.material?.metallic ?? 0.5,
      roughness: vr.material?.roughness ?? 0.5,
    };

    return {
      type: primitive,
      args: computeArgsFromDims(primitive, dims),
      material,
      source: 'visual_representation',
      side,
    };
  }

  // ============================================================
  // 1. base_form (игра, installed_modules)
  // ============================================================
  let primitive = null;
  let source = 'default';

  const baseForm = module.base_form || module.base_form_spec?.название;
  if (baseForm && SHAPE_TO_PRIMITIVE[baseForm]) {
    primitive = SHAPE_TO_PRIMITIVE[baseForm];
    source = 'base_form';
  }

  // ============================================================
  // 2. purpose (fallback)
  // ============================================================
  if (!primitive) {
    const purposeShape = PURPOSE_TO_SHAPE[module.purpose];
    if (purposeShape && SHAPE_TO_PRIMITIVE[purposeShape]) {
      primitive = SHAPE_TO_PRIMITIVE[purposeShape];
      source = 'purpose';
    }
  }

  // ============================================================
  // 3. default
  // ============================================================
  if (!primitive) {
    primitive = 'box';
    source = 'default';
  }

  // Размер
  const side = computeSide(module);
  const args = computeArgs(primitive, side);

  // Материал
  const color =
    module.visual?.colors?.primary ||
    COLOR_BY_PURPOSE[module.purpose] ||
    COLOR_BY_PURPOSE.default;

  const hasEmissive =
    Array.isArray(module.visual?.emissive_elements) &&
    module.visual.emissive_elements.length > 0;

  const material = {
    color,
    emissive: hasEmissive ? color : null,
    emissiveIntensity: hasEmissive ? 0.3 : 0,
    metalness: 0.5,
    roughness: 0.5,
  };

  return { type: primitive, args, material, source, side };
}

// ============================================================================
// 7. РАЗМЕР
// ============================================================================

function computeSide(module) {
  // Приоритет 1: base_stats.volume_m3 → cbrt
  const volume = module.base_stats?.volume_m3;
  if (typeof volume === 'number' && volume > 0) {
    return Math.max(0.5, Math.cbrt(volume));
  }

  // Приоритет 2: slot_size → SLOT_VOLUMES → cbrt
  const slotVolume = SLOT_VOLUMES[module.slot_size];
  if (typeof slotVolume === 'number' && slotVolume > 0) {
    return Math.max(0.5, Math.cbrt(slotVolume));
  }

  // Приоритет 3: default
  return 2;
}

// ============================================================================
// 8. ARGS (параметры Three.js geometry) для base_form / purpose / default
// ============================================================================

function computeArgs(primitive, side) {
  switch (primitive) {
    case 'box':
      return [side, side, side];
    case 'sphere':
      return [side / 2, 16, 16];
    case 'cylinder':
      return [side / 2, side / 2, side, 12];
    case 'cone':
      return [side / 2, side, 12];
    case 'torus': {
      const radius = side / 2;
      const tube = Math.max(0.1, side / 10);
      return [radius, tube, 8, 16];
    }
    default:
      return [side, side, side];
  }
}

// ============================================================================
// 9. JSX-РЕНДЕР (используется в ModulePrimitive и Module3DViewer)
// ============================================================================

/**
 * Возвращает JSX-геометрию для <mesh>.
 * ⚠️ Это единственное место, где используется React (JSX).
 *
 * @param {{type, args}} geom
 * @returns {JSX.Element}
 */
export function renderGeometryJSX(geom) {
  if (!geom || !geom.type || !Array.isArray(geom.args)) {
    return <boxGeometry args={[2, 2, 2]} />;
  }

  switch (geom.type) {
    case 'box':
      return <boxGeometry args={geom.args} />;
    case 'sphere':
      return <sphereGeometry args={geom.args} />;
    case 'cylinder':
      return <cylinderGeometry args={geom.args} />;
    case 'cone':
      return <coneGeometry args={geom.args} />;
    case 'torus':
      return <torusGeometry args={geom.args} />;
    default:
      return <boxGeometry args={[2, 2, 2]} />;
  }
}

// ============================================================================
// 10. DEFAULT
// ============================================================================

function defaultGeometry() {
  return {
    type: 'box',
    args: [2, 2, 2],
    material: {
      color: COLOR_BY_PURPOSE.default,
      emissive: null,
      emissiveIntensity: 0,
      metalness: 0.5,
      roughness: 0.5,
    },
    source: 'default',
    side: 2,
  };
}

export default getModuleGeometry;
